// Disposable email domain blocklist
// Updated: 2026-01-17
// Sources: YOPmail, Guerrilla Mail, Temp-Mail, 10-Minute Mail, and other common providers
const DISPOSABLE_EMAIL_DOMAINS = [
  // YOPmail domains
  'yopmail.com', 'yopmail.fr', 'yopmail.net', 'cool.fr.nf', 'jetable.fr.nf',
  'nospam.ze.tc', 'nomail.xl.cx', 'mega.zik.dj', 'speed.1s.fr', 'courriel.fr.nf',
  
  // Guerrilla Mail domains
  'guerrillamail.com', 'guerrillamailblock.com', 'sharklasers.com', 'grr.la',
  'guerrillamail.net', 'guerrillamail.org', 'guerrillamail.biz', 'spam4.me',
  'guerrillamail.de', 'pokemail.net',
  
  // Temp-Mail domains
  'temp-mail.org', 'temp-mail.io', 'tempmail.com', 'tempmail.net', 'tempmail.org',
  'tmpmail.org', 'tmpmail.net', 'tempinbox.com', 'tempr.email', 'tempmailaddress.com',
  
  // 10-Minute Mail
  '10minutemail.com', '10minutemail.net', '10minutemail.org', '10minemail.com',
  
  // Other popular disposable email services
  'mailinator.com', 'maildrop.cc', 'throwaway.email', 'getnada.com', 'trashmail.com',
  'fakeinbox.com', 'emailondeck.com', 'mail.tm', 'mohmal.com', 'dispostable.com',
  'throwawaymail.com', 'mintemail.com', 'mytemp.email', 'emailfake.com',
  'mailnesia.com', 'mailcatch.com', 'burnermail.io', 'spamgourmet.com',
  'anonbox.net', 'anonymousemail.me', 'deadaddress.com', 'getairmail.com',
  'mailsac.com', 'mailtemp.info', 'sharklasers.com', 'spam.la',
  'spambox.us', 'spamfree24.org', 'spamhole.com', 'spamify.com',
  'trashmail.ws', 'wegwerfmail.de', 'wegwerfemail.de', 'zehnminutenmail.de',
  
  // Additional common ones
  'mailinator2.com', 'sogetthis.com', 'mailin8r.com', 'mailinator.net',
  'notmailinator.com', 'veryrealemail.com', 'safetymail.info', 'spamhereplease.com',
  'binkmail.com', 'bobmail.info', 'chammy.info', 'devnullmail.com',
  'letthemeatspam.com', 'mailinater.com', 'soodonims.com', 'spamthisplease.com',
  'thisisnotmyrealemail.com', 'tradermail.info', 'veryrealemail.com',
];

/**
 * Check if an email address uses a disposable/temporary email domain
 */
export function isDisposableEmail(email: string): boolean {
  const domain = email.toLowerCase().split('@')[1];
  if (!domain) return false;
  
  return DISPOSABLE_EMAIL_DOMAINS.includes(domain);
}

/**
 * Validate email format and check for disposable domains
 */
export function validateEmail(email: string): { valid: boolean; error?: string } {
  // Basic format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!email || email.trim().length === 0) {
    return { valid: false, error: 'Email is required' };
  }
  
  if (!emailRegex.test(email)) {
    return { valid: false, error: 'Invalid email format' };
  }
  
  // Check for disposable email
  if (isDisposableEmail(email)) {
    return { 
      valid: false, 
      error: 'Disposable email addresses are not allowed. Please use a permanent email address.' 
    };
  }
  
  return { valid: true };
}

/**
 * Validate password strength
 */
export function validatePassword(password: string): { valid: boolean; error?: string } {
  if (!password || password.length === 0) {
    return { valid: false, error: 'Password is required' };
  }
  
  if (password.length < 6) {
    return { valid: false, error: 'Password must be at least 6 characters' };
  }
  
  return { valid: true };
}
